import urijs from 'urijs';
/**
 * The different routes the module supports are used from a lot of different services and
 * containers, so the idea of this service is for it to be a helper that would be able to
 * generate them with simple methods.
 * This service only contains methods for those routes the services/containers actually use.
 * @ngesdoc service
 * @ngname authRoutesManager
 * @ngmodule auth
 */
class AuthRoutesManager {
  /**
   * @param {AuthRoutes} AUTH_ROUTES The dictionary with all the defined routes.
   */
  constructor(AUTH_ROUTES) {
    'ngInject';

    /**
     * A local reference for the `AUTH_ROUTES` constant.
     * @type {AuthRoutes}
     */
    this.AUTH_ROUTES = AUTH_ROUTES;
  }
  /**
   * Checks whether a given route matches with the one for the login screen. This is used
   * when validating unauthorized responses, since is normal to get a `401` on a failed login
   * attempt.
   * @param {string} route The route to match.
   * @return {boolean}
   */
  isLogin(route) {
    const loginRoute = this.AUTH_ROUTES.login;
    const regex = new RegExp(`${loginRoute}(?:(?:/)?$|\\?|#)`, 'i');
    return route.match(regex);
  }
  /**
   * Gets the route for the login screen.
   * @param {Boolean} [state=false] Whether or not the SSO state was generated.
   * @param {String}  [error='']    An error code for the login screen to show.
   * @return {string}
   */
  login(state = false, error = '') {
    let uri = urijs(this.AUTH_ROUTES.login)
    .addQuery('state', state.toString());

    if (error) {
      uri = uri.addQuery('error', error);
    }

    return uri.toString();
  }
  /**
   * Gets the route for the login screen with redirect_manifest param in false.
   * @param {Boolean} [state=false] Whether or not the SSO state was generated.
   * @return {string}
   */
  loginNORedirect(state = false) {
    const uri = urijs(this.AUTH_ROUTES.login)
    .addQuery('state', state.toString())
    .addQuery('redirect_manifest', false);

    return uri.toString();
  }
  /**
   * Gets the route to the _"remember your password screen"_.
   * @param {string} state Whether or not the SSO state was generated.
   * @return {string}
   */
  rememberPassword(state) {
    return urijs(this.AUTH_ROUTES.rememberPassword)
    .addQuery('state', state.toString())
    .toString();
  }
  /**
   * Gets the route to the success screen.
   * @param {string} token The obtained user authentication token.
   * @return {string}
   */
  success(token) {
    return urijs(this.AUTH_ROUTES.success)
    .addQuery('token', token)
    .toString();
  }
}

export default AuthRoutesManager;
